// UI strings for the course as a whole (docs/COURSE.md): the goal and the schedule in the frontier
// strip, the unlock rule on the lesson list, the "Tämä viikko" strip in Tänään, the week page
// (Kuuntele and Puhu of the current lesson) and the Kielioppi lesson labels.
// Finnish (default) and English side by side; en has the same shape.
// Style: docs/FINNISH-STYLE.md (te-muoto for both, no internal jargon, "1 oppitunti", "2 oppituntia").
import type { PronunciationFocus } from '@valyrio/engine/types';
import { defineArea } from '../area';

const fiLessons = (n: number) => (n === 1 ? '1 oppitunti' : `${n} oppituntia`);
const enLessons = (n: number) => (n === 1 ? '1 lesson' : `${n} lessons`);

export const courseFi = {
  /** The goal the course is paced to (pace.goalDate). */
  goal: 'Halloween 2027',
  onSchedule: 'aikataulussa',
  behind: (n: number) => `${fiLessons(n)} jäljessä`,
  allOpen: 'kaikki oppitunnit avattu',
  /** The next lesson's date has come; it opens once these pass the current one. */
  nextWhenBoth: (n: number) => `seuraava, kun molemmat läpäisette oppitunnin ${n}`,
  nextWhenMe: (n: number) => `seuraava, kun läpäiset oppitunnin ${n}`,
  nextWhenOther: (name: string, n: number) => `seuraava, kun ${name} läpäisee oppitunnin ${n}`,
  /** Lesson list: a lesson whose date has come but waits for the one before. */
  opensWhenPassed: (n: number) => `Avautuu, kun olette molemmat läpäisseet oppitunnin ${n}.`,
  /** The same in the lesson list's meta line (lower case, no full stop). */
  opensWhenPassedMeta: (n: number) => `avautuu, kun olette molemmat läpäisseet oppitunnin ${n}`,
  opensOn: (date: string) => `Avautuu ${date}, kun olette molemmat läpäisseet edellisen.`,
  duolingo: (units: string) => `Duolingo: ${units}`,
  week: {
    title: 'Tämä viikko',
    lesson: (n: number) => `Oppitunti ${n}`,
    steps: {
      lesson: 'Tunti',
      practice: 'Harjoitus',
      listen: 'Kuuntele',
      speak: 'Puhu',
      letter: 'Kirje',
      together: 'Yhdessä',
    },
    done: 'tehty',
    notDone: 'tekemättä',
    lines: (n: number) => (n === 1 ? '1 repliikki' : `${n} repliikkiä`),
  },
  /** The week page (/week): this lesson's show lines and pronunciation focus. */
  page: {
    title: (n: number) => `Viikko ${n}`,
    listenH: 'Kuuntele',
    speakH: 'Puhu',
    letterH: 'Viikon kirje',
    writeLetter: 'Kirjoita kirje',
    noLines: 'Tällä oppitunnilla ei ole sarjan repliikkejä.',
    focus: 'Painopiste',
    noLesson: 'Ensimmäinen oppitunti ei ole vielä avautunut.',
  },
  focus: {
    stress: 'Paino',
    'long-vowels': 'Pitkät vokaalit',
    y: 'y ja ȳ',
    r: 'r ja rh',
    z: 'Soinnillinen z',
    j: 'j',
    q: 'q',
    gh: 'gh',
    palatals: 'lj ja ñ',
    diphthongs: 'Diftongit',
    lengthening: '”Ja” pitkällä vokaalilla',
    'command-stress': 'Käskyn paino',
    rhythm: 'Puherytmi',
  } satisfies Record<PronunciationFocus, string>,
  /** Under a Kielioppi topic: "Opit tämän oppitunnilla 1 (läpäisty); myös oppitunnilla 11 ja oppitunnilla 19." */
  grammar: {
    taughtIn: 'Opit tämän',
    /** One link each: the whole phrase, not a bare number. */
    lessonAt: (n: number) => `oppitunnilla ${n}`,
    passed: 'läpäisty',
    also: 'myös',
    and: 'ja',
  },
  /** The letter prompt in Kirjeet when writing the week's letter. */
  letterPrompt: (n: number) => `Oppitunnin ${n} kirje`,
};

export const courseEn: typeof courseFi = {
  goal: 'Halloween 2027',
  onSchedule: 'on schedule',
  behind: (n: number) => `${enLessons(n)} behind`,
  allOpen: 'every lesson open',
  nextWhenBoth: (n: number) => `next once you have both passed lesson ${n}`,
  nextWhenMe: (n: number) => `next once you pass lesson ${n}`,
  nextWhenOther: (name: string, n: number) => `next once ${name} passes lesson ${n}`,
  opensWhenPassed: (n: number) => `Opens once you have both passed lesson ${n}.`,
  opensWhenPassedMeta: (n: number) => `opens once you have both passed lesson ${n}`,
  opensOn: (date: string) => `Opens ${date} once you have both passed the one before.`,
  duolingo: (units: string) => `Duolingo: ${units}`,
  week: {
    title: 'This week',
    lesson: (n: number) => `Lesson ${n}`,
    steps: {
      lesson: 'Lesson',
      practice: 'Practice',
      listen: 'Listen',
      speak: 'Speak',
      letter: 'Letter',
      together: 'Together',
    },
    done: 'done',
    notDone: 'not done',
    lines: (n: number) => (n === 1 ? '1 line' : `${n} lines`),
  },
  page: {
    title: (n: number) => `Week ${n}`,
    listenH: 'Listen',
    speakH: 'Speak',
    letterH: 'The week’s letter',
    writeLetter: 'Write the letter',
    noLines: 'This lesson has no lines from the shows.',
    focus: 'Focus',
    noLesson: 'The first lesson has not opened yet.',
  },
  focus: {
    stress: 'Stress',
    'long-vowels': 'Long vowels',
    y: 'y and ȳ',
    r: 'r and rh',
    z: 'Voiced z',
    j: 'j',
    q: 'q',
    gh: 'gh',
    palatals: 'lj and ñ',
    diphthongs: 'Diphthongs',
    lengthening: '“And” as a long vowel',
    'command-stress': 'Stress in commands',
    rhythm: 'Rhythm of speech',
  },
  grammar: {
    taughtIn: 'You learn this',
    lessonAt: (n: number) => `in lesson ${n}`,
    passed: 'passed',
    also: 'also',
    and: 'and',
  },
  letterPrompt: (n: number) => `Letter for lesson ${n}`,
};

// Messages.course (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('course', courseFi, courseEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof courseFi> {}
}
